import React from 'react';
import { StringFormatter } from '@agoda/react-handyman';
import { Typography } from '@kite-js/experimental';
import { FlexDirection, FinalRateProps, JustifyContent, Typography as DroneTypography } from '@drone-js/core';
import { TypographyProps } from '@kite-js/experimental/lib/Typography';
import { PriceAmount, PriceAmountWithPoints, PriceAmountWithPointsOnSameLine } from 'component/common/Price/PriceAmount';
import { PriceDisplayBehavior, PriceDisplayType } from 'core/type/behaviors.types';
import { mapPointBreakupByOffer, PointBreakupProps } from 'gql/service/graph/activityService';
import { useSelector } from 'react-redux';
import { selectBehaviorFromBootstrap, selectConfigFromBootstrap, selectLanguageFromBootstrap } from 'store/slice/bootstrap';
import { PricingSummary } from 'gql/autogen/PricingSummary';
import PriceWithBadgePoints from 'component/common/Price/PriceWithBadgePoints';
import { AlignItems } from '@drone-js/core/src/types/Types';
import { useFeatureEnabled } from 'component/common/Experiment/Feature';
import { FeatureName } from 'core/constant/FeatureName';
import { getPriceDecimal } from 'core/utils/roundNumberWithDecimal';

export const MIN_PRICE_FOR_ROUNDING = 5;
const PriceWithPointsOnNextLineAmountStyle: TypographyProps = { variant: 'heading2' };
const PriceWithPointsOnSameLineAmountStyle: TypographyProps = { variant: 'heading2' };
const PriceStyle: TypographyProps = { variant: 'body1' };
const PriceAndPointsContainerStyle = { display: 'flex', justifyContent: 'end' };

interface PriceDisplayProps {
    pointBreakup: PointBreakupProps;
    // eslint-disable-next-line react/no-unused-prop-types
    activityId?: number;
    // eslint-disable-next-line react/no-unused-prop-types
    isDisplayPricePerPax?: boolean;
    // eslint-disable-next-line react/no-unused-prop-types
    isShowPrefix?: boolean;
    decimal?: number;
    priceDirection?: FlexDirection;
    priceJustifyContent?: JustifyContent;
    priceAlignItems?: AlignItems;
    finalPriceSize?: FinalRateProps['size'];
    isMobileScreen?: boolean;
    isOptionDisabled?: boolean;
    showPointsBadge?: boolean;
}

type CommonOmittedProps = 'isOptionDisabled' | 'isMobileScreen';

const PriceWithPointsOnNextLine: React.FC<Omit<PriceDisplayProps, CommonOmittedProps>> = (props) => {
    const pointBreakup = props.pointBreakup;
    if (pointBreakup && pointBreakup.points === 0) {
        return (
            <PriceAmount
                activityId={props.activityId}
                amount={pointBreakup.amount}
                currency={pointBreakup.currency}
                decimal={props.decimal}
                priceDirection={props.priceDirection}
                priceJustifyContent={props.priceJustifyContent}
                finalPriceSize={props.finalPriceSize}
            />
        );
    }
    return (
        <PriceAmountWithPoints
            amount={pointBreakup.amount}
            corAmount={pointBreakup.corAmount}
            formattedPoints={pointBreakup.formattedPoints}
            currency={pointBreakup.currency}
            overrideAmountProps={PriceWithPointsOnNextLineAmountStyle}
            payablePrice={pointBreakup.payablePrice}
            formattedPayablePrice={pointBreakup.formattedPayablePrice}
            decimal={props.decimal}
            priceDirection={props.priceDirection}
            priceJustifyContent={props.priceJustifyContent}
        />
    );
};
PriceWithPointsOnNextLine.displayName = 'PriceWithPointsOnNextLine';

const PriceWithPointsOnSameLine: React.FC<Omit<PriceDisplayProps, Exclude<CommonOmittedProps, 'isMobileScreen'>>> = (props) => {
    const pointBreakup = props.pointBreakup;
    if (pointBreakup && pointBreakup.points === 0) {
        return (
            <PriceAmount
                activityId={props.activityId}
                amount={pointBreakup.amount}
                currency={pointBreakup.currency}
                decimal={props.decimal}
                priceDirection={props.priceDirection}
                priceJustifyContent={props.priceJustifyContent}
                finalPriceSize={props.finalPriceSize}
            />
        );
    }
    return (
        <PriceAmountWithPointsOnSameLine
            amount={pointBreakup.amount}
            corAmount={pointBreakup.corAmount}
            formattedPoints={pointBreakup.formattedPoints}
            currency={pointBreakup.currency}
            overrideAmountProps={PriceWithPointsOnSameLineAmountStyle}
            payablePrice={pointBreakup.payablePrice}
            formattedPayablePrice={pointBreakup.formattedPayablePrice}
            decimal={props.decimal}
            priceDirection={props.priceDirection}
            priceJustifyContent={props.priceJustifyContent}
            isMobileScreen={props.isMobileScreen}
        />
    );
};
PriceWithPointsOnSameLine.displayName = 'PriceWithPointsOnSameLine';

const PriceAndPoints: React.FC<Omit<PriceDisplayProps, CommonOmittedProps>> = (props) => {
    const { behavior, cms } = useSelector(selectConfigFromBootstrap);
    const { burn: commonBurnBehaviour, price: commonPriceBehaviour } = behavior.common;
    const pointBreakup = props.pointBreakup;
    const pointsSupportCms =
        commonBurnBehaviour.pointsWithExtraCms && pointBreakup.payablePrice > 0 ? cms[commonBurnBehaviour.pointsWithExtraCms] : cms[commonBurnBehaviour.pointsCms];

    const priceText = StringFormatter.formatToJsx(
        pointsSupportCms,
        pointBreakup.formattedPoints,
        <PriceAmount
            activityId={props.activityId}
            amount={pointBreakup.amount}
            currency={pointBreakup.currency}
            overrideAmountProps={PriceStyle}
            decimal={props.decimal}
            priceDirection={props.priceDirection}
            priceJustifyContent={props.priceJustifyContent}
            finalPriceSize={props.finalPriceSize}
        />,
    );

    return (
        <Typography
            as="span"
            data-element-name="activities-points"
            data-price-type="activities-price-with-points"
            variant="paragraph1"
            color="secondary"
            textAlign="left"
            style={PriceAndPointsContainerStyle}
        >
            <Typography as="span" variant="body2" color={500} me="XS">
                {props.isShowPrefix === false && (
                    <Typography as="span" key="PriceAndPoints" variant="paragraph1" color="secondary">
                        {priceText}
                    </Typography>
                )}
                {/* eslint-disable-next-line no-restricted-syntax */}
                {props.isShowPrefix === undefined &&
                    StringFormatter.formatToJsx(
                        cms[commonPriceBehaviour.prefixCms],
                        <Typography as="span" key="PriceAndPoints" variant="paragraph1" color="secondary">
                            {priceText}
                        </Typography>,
                    )}
            </Typography>
        </Typography>
    );
};
PriceAndPoints.displayName = 'PriceAndPoints';

const PriceWithoutPoints: React.FC<Omit<PriceDisplayProps, CommonOmittedProps>> = (props) => {
    const { behavior, cms } = useSelector(selectConfigFromBootstrap);
    const { amount, corAmount, currency } = props.pointBreakup;
    if (props.isDisplayPricePerPax) {
        return (
            <Typography variant="caption2">
                {StringFormatter.formatToJsx(
                    cms[190707],
                    <PriceAmount
                        activityId={props.activityId}
                        key="PriceWithoutPoints"
                        amount={amount}
                        corAmount={corAmount}
                        currency={currency}
                        overrideAmountProps={PriceWithPointsOnNextLineAmountStyle}
                        decimal={props.decimal}
                        priceDirection={props.priceDirection}
                        priceJustifyContent={props.priceJustifyContent}
                        priceAlignItems={props.priceAlignItems}
                    />,
                )}
            </Typography>
        );
    }
    return (
        <PriceAmount
            activityId={props.activityId}
            amount={amount}
            corAmount={corAmount}
            currency={currency}
            prefix={props.isShowPrefix === false ? undefined : cms[behavior.common.price.prefixCms]}
            overrideAmountProps={PriceStyle}
            decimal={props.decimal}
            priceDirection={props.priceDirection}
            priceJustifyContent={props.priceJustifyContent}
            finalPriceSize={props.finalPriceSize}
            priceAlignItems={props.priceAlignItems}
        />
    );
};
PriceWithoutPoints.displayName = 'PriceWithoutPoints';

const PriceDisplayComponents: { [key in PriceDisplayBehavior]: React.FC<PriceDisplayProps> } = {
    [PriceDisplayBehavior.PriceWithPointsOnNextLine]: PriceWithPointsOnNextLine,
    [PriceDisplayBehavior.PriceAndPoints]: PriceAndPoints,
    [PriceDisplayBehavior.Price]: PriceWithoutPoints,
    [PriceDisplayBehavior.PriceWithPointsOnSameLine]: PriceWithPointsOnSameLine,
    [PriceDisplayBehavior.PriceWithBadgePoints]: PriceWithBadgePoints,
};

interface PriceProps extends Omit<PriceDisplayProps, 'amount' | 'pointBreakup'> {
    pricingSummary?: PricingSummary;
    priceDisplay?: PriceDisplayType;
    isPriceRoundingUp?: boolean;
    priceDirection?: FlexDirection;
    priceJustifyContent?: JustifyContent;
    displayBehavior?: PriceDisplayBehavior;
    isMobileScreen?: boolean;
    isSchedulesEmpty?: boolean;
}

export const Price: React.FC<PriceProps> = (props) => {
    const language = useSelector(selectLanguageFromBootstrap);
    const behavior = useSelector(selectBehaviorFromBootstrap);
    const config = useSelector(selectConfigFromBootstrap);
    const cms = config.cms;
    const isImproveUnAvailableOnSelectedDateEnabled = useFeatureEnabled(FeatureName.ImproveUnavailableOffer, config);

    // No pricingSummary
    if (!props.pricingSummary) return null;

    const displayBehavior = props.displayBehavior || behavior.common.price.displayBehavior;
    const corBehavior = behavior.common.price.crossedOutRateBehavior;
    const pointBreakup = mapPointBreakupByOffer(language.locale, props.pricingSummary, props.priceDisplay, displayBehavior, corBehavior);

    let decimal: number | undefined;
    if (props.isPriceRoundingUp) {
        if (pointBreakup.amount > MIN_PRICE_FOR_ROUNDING) {
            pointBreakup.amount = Math.ceil(pointBreakup.amount);
            pointBreakup.corAmount = Math.ceil(pointBreakup.corAmount);
            if (pointBreakup.corAmount <= pointBreakup.amount) {
                pointBreakup.corAmount = 0;
            }
        }
        decimal = getPriceDecimal(pointBreakup.amount, MIN_PRICE_FOR_ROUNDING);
    }

    if (pointBreakup.amount <= 0 && pointBreakup.points <= 0) {
        return (
            <DroneTypography data-testid="activities-no-price" variant="caption-default" textColor="base.subtle">
                {isImproveUnAvailableOnSelectedDateEnabled ? (props.isSchedulesEmpty ? cms[343876] : cms[343497]) : cms[243875]}
            </DroneTypography>
        );
    }

    const PriceDisplayComponent = PriceDisplayComponents[displayBehavior];
    return (
        <div data-testid="activities-price-container">
            <PriceDisplayComponent
                activityId={props.activityId}
                pointBreakup={pointBreakup}
                isDisplayPricePerPax={props.isDisplayPricePerPax}
                isShowPrefix={props.isShowPrefix}
                decimal={decimal}
                priceDirection={props.priceDirection}
                priceJustifyContent={props.priceJustifyContent}
                finalPriceSize={props.finalPriceSize}
                isMobileScreen={props.isMobileScreen}
                isOptionDisabled={props.isOptionDisabled}
                showPointsBadge={props.showPointsBadge}
                priceAlignItems={props.priceAlignItems}
            />
        </div>
    );
};
Price.displayName = 'Price';
